import { createContext, useContext, useState } from "react";
import { CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

export const PRIMARY = "#018D4C";

export const inputCls =
  "w-full px-3 py-2.5 text-sm border border-zinc-100 bg-white focus:outline-none focus:ring-2 placeholder-zinc-400 rounded-lg";
export const ringStyle = { "--tw-ring-color": PRIMARY };

// Every field POST /settings accepts. The backend upserts the whole row from
// SettingsCreate (missing fields fall back to defaults), so each section must
// save the full form.
// CMS credentials/config aren't here — they live in cms_connections and are
// edited on the CMS Integrations pages (GET/PUT /cms/connection).
export const EMPTY_FORM = {
  astro_site_base_url: "", astro_default_locale: "en", astro_url_prefix: "",
  auto_sync_enabled: false, sync_interval_hours: 24,
  serper_api_key: "",
  deepl_api_key: "",
  openai_api_key: "",
  business_context: "",
  article_objective: "",
  buyer_personas: [],
  key_pages: [],
  competitors: [],
  target_country: "", // ISO alpha-2; "" = not set
};

export function formFromSettings(s) {
  return Object.fromEntries(
    Object.entries(EMPTY_FORM).map(([k, blank]) => [k, s?.[k] ?? blank])
  );
}

export const SettingsContext = createContext(null);
export const useSettings = () => useContext(SettingsContext);

export function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-100">
        <Icon size={14} strokeWidth={2} className="text-zinc-900" />
        <div>
          <p className="text-sm font-bold text-zinc-900">{title}</p>
          {subtitle && <p className="text-[10px] text-zinc-500">{subtitle}</p>}
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

export function Label({ children, required, optional, className = "mb-1" }) {
  return (
    <label className={`block text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500 ${className}`}>
      {children}
      {required && <span className="text-red-500"> *</span>}
      {optional && <span className="text-zinc-400 font-normal normal-case"> (optional)</span>}
    </label>
  );
}

export function Hint({ children }) {
  return <p className="text-[9px] text-zinc-400 mt-1">{children}</p>;
}

export function Field({ label, required, optional, hint, mono, ...inputProps }) {
  return (
    <div>
      <Label required={required} optional={optional}>{label}</Label>
      <input
        type="text" required={required} {...inputProps}
        className={`${inputCls} ${mono ? "font-mono" : ""}`}
        style={ringStyle}
      />
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

export function SecretField({ label, required, optional, hint, ...inputProps }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label required={required} optional={optional}>{label}</Label>
      <div className="relative">
        <input
          {...inputProps} type={show ? "text" : "password"}
          className={`${inputCls} pr-9 font-mono`}
          style={ringStyle}
        />
        <button type="button" onClick={() => setShow(v => !v)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
          title={show ? "Hide" : "Show"}
        >
          {show ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
        </button>
      </div>
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

export function ResultBanner({ result, testId }) {
  if (!result) return null;
  return (
    <div data-testid={testId}
      className={`flex items-center gap-2 px-4 py-3 text-xs border rounded-lg ${result.success ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}
    >
      {result.success ? <CheckCircle size={14} strokeWidth={2} /> : <XCircle size={14} strokeWidth={2} />}
      {result.message}
    </div>
  );
}

// Section-level <form> that saves the whole shared settings form.
export function SettingsForm({ children, testId = "settings-form" }) {
  const { save, saving, loading, dirty } = useSettings();
  const [result, setResult] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    setResult(null);
    setResult(await save());
  };

  return (
    <form onSubmit={handleSubmit} data-testid={testId} className="space-y-4">
      {children}
      <ResultBanner result={result} testId="save-settings-result" />
      <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-[10px] text-amber-600 font-bold">Unsaved changes</span>}
        <button type="submit" disabled={saving || loading}
          data-testid="save-settings-button"
          className="px-5 py-2.5 text-xs font-bold text-white disabled:opacity-40 transition-colors rounded-lg hover:opacity-90"
          style={{ backgroundColor: PRIMARY }}
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </form>
  );
}
